import buildParams from 'src/internal/analytics/buildParams';
import {getDefaultRpcInstance} from 'src/internal/rpc/defaultRpc';
import {v4 as uuid} from 'uuid';

import type {
  IAnalytics,
  ImperativeAnalytics,
} from 'src/internal/analytics/types';

type AnalyticsMethodName = keyof IAnalytics;

const rpcMethodMap = {
  action: 'analyticsAction',
  link: 'analyticsLink',
  modal: 'analyticsModal',
  track: 'analyticsTrack',
  viewed: 'analyticsViewed',
} as const;

const method = <TMethod extends AnalyticsMethodName>(
  analyticsMethodName: TMethod,
): ImperativeAnalytics[TMethod] => {
  return (config, eventName, parameters): void => {
    if (!config || config.analytics?.disabled) {
      return;
    }

    // This dual Reporting config is just temporary.
    const currentParams = config._tmp_dualReporting
      ? {...parameters!, _tdr_id: uuid()}
      : parameters;

    const injectedMethod =
      config._internal_observability?.analytics?.[analyticsMethodName];

    if (injectedMethod) {
      injectedMethod(config, eventName, buildParams(config, currentParams));

      if (!config._tmp_dualReporting) {
        return;
      }
    }

    const rpcClient = config.rpc ?? getDefaultRpcInstance().client;
    const rpcMethod = rpcMethodMap[analyticsMethodName];

    const eventNameWithPotentialDualReporting = config._tmp_dualReporting
      ? `_tdr.${eventName}`
      : eventName;

    rpcClient.call(
      rpcMethod,
      // @ts-expect-error - TS2345 - Argument of type '[Readonly<{ service?: string | undefined; project: ProjectName; page?: string | undefined; pageViewToken?: string | undefined; release?: string | undefined; userId?: string | undefined; ... 5 more ...; _tmp_dualReporting?: boolean | undefined; }>, string, AnalyticsParams<...> | undefined]' is not assignable to parameter of type 'ObservabilityRpcMethods[{ readonly action: "analyticsAction"; readonly link: "analyticsLink"; readonly modal: "analyticsModal"; readonly track: "analyticsTrack"; readonly viewed: "analyticsViewed"; }[TMethod]]["args"]'. | Type '[Readonly<{ service?: string | undefined; project: ProjectName; page?: string | undefined; pageViewToken?: string | undefined; release?: string | undefined; userId?: string | undefined; ... 5 more ...; _tmp_dualReporting?: boolean | undefined; }>, string, AnalyticsParams<...> | undefined]' is not assignable to type '[config: Readonly<{ service?: string | undefined; project: ProjectName; page?: string | undefined; pageViewToken?: string | undefined; release?: string | undefined; userId?: string | undefined; ... 5 more ...; _tmp_dualReporting?: boolean | undefined; }> | null, eventName: string, parameters?: { ...; } | undefined]'. | Type at position 2 in source is not compatible with type at position 2 in target. | Type 'AnalyticsParams<T> | undefined' is not assignable to type '{ [key: string]: unknown; client_id?: undefined; created?: undefined; event_id?: undefined; event_name?: undefined; action_name?: undefined; analytics_ua?: undefined; cid?: undefined; domain?: undefined; ... 8 more ...; viewed_name?: undefined; } | undefined'. | Type 'AnalyticsParams<T>' is not assignable to type '{ [key: string]: unknown; client_id?: undefined; created?: undefined; event_id?: undefined; event_name?: undefined; action_name?: undefined; analytics_ua?: undefined; cid?: undefined; domain?: undefined; ... 8 more ...; viewed_name?: undefined; } | undefined'. | Type '{ page: string; release: string; service: string; project: ProjectName; error_message: string; error_handled: string; error_issue_id: string; error_issue_url: string; } | { [key: string]: unknown; ... 16 more ...; viewed_name?: undefined; }' is not assignable to type '{ [key: string]: unknown; client_id?: undefined; created?: undefined; event_id?: undefined; event_name?: undefined; action_name?: undefined; analytics_ua?: undefined; cid?: undefined; domain?: undefined; ... 8 more ...; viewed_name?: undefined; } | undefined'.
      config,
      eventNameWithPotentialDualReporting,
      currentParams,
    );
  };
};

/**
 * @see https://sail.stripe.me/observability/api/useAnalytics#action
 */
export const action = method('action');

/**
 * @see https://sail.stripe.me/observability/api/useAnalytics#link
 */
export const link = method('link');

/**
 * @see https://sail.stripe.me/observability/api/useAnalytics#modal
 */
export const modal = method('modal');

/**
 * @see https://sail.stripe.me/observability/api/useAnalytics#track
 */
export const track = method('track');

/**
 * @see https://sail.stripe.me/observability/api/useAnalytics#viewed
 */
export const viewed = method('viewed');
